import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import Badge from '../../components/common/Badge';
import Loader from '../../components/common/Loader';
import { getCompanyApplications, updateApplicationStatus, getCompanyJobs } from '../../api/company';
import { useToast } from '../../context/ToastContext';
import { STATUS } from '../../config/constants';

const CompanyApplicants = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterJob, setFilterJob] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const { addToast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, jobsRes] = await Promise.all([
        getCompanyApplications(),
        getCompanyJobs()
      ]);
      setApplications(appsRes.data || []);
      setJobs(jobsRes.data || []);
    } catch (err) {
      addToast('Failed to load applicants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appId, newStatus) => {
    if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`)) return;
    
    try {
      await updateApplicationStatus(appId, newStatus);
      addToast('Status updated successfully', 'success');
      // Update local state instead of refetching everything
      setApplications(apps => apps.map(app => 
        app.id === appId ? { ...app, status: newStatus } : app
      ));
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to update status', 'error');
    }
  };

  const filteredApps = applications.filter(app => {
    const matchJob = filterJob === 'all' || app.opportunity_id === parseInt(filterJob);
    const matchStatus = filterStatus === 'all' || app.status === filterStatus;
    return matchJob && matchStatus;
  });

  return (
    <div className="page-wrapper dashboard-page">
      <Navbar />
      
      <main className="container pt-8 pb-20 flex-1">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="heading-1">Applicants</h1>
            <p className="subheading">Manage and track candidates for your job postings.</p>
          </div>
        </div>

        <div className="card-elevated">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-gray-100">
            <div className="w-full sm:w-auto">
              <label className="text-xs font-semi text-gray-500 uppercase tracking-wider mb-1 block">Filter by Job</label>
              <select 
                className="form-input select" 
                value={filterJob} 
                onChange={e => setFilterJob(e.target.value)}
              >
                <option value="all">All Jobs</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>
            
            <div className="w-full sm:w-auto">
              <label className="text-xs font-semi text-gray-500 uppercase tracking-wider mb-1 block">Filter by Status</label>
              <select 
                className="form-input select" 
                value={filterStatus} 
                onChange={e => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {Object.values(STATUS).map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="py-20 flex justify-center"><Loader /></div>
          ) : filteredApps.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3 className="empty-title">No applicants found</h3>
              <p className="empty-desc">Try adjusting your filters or wait for candidates to apply.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>College Info</th>
                    <th>Applied For</th>
                    <th>Date</th>
                    <th>Current Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map(app => (
                    <tr key={app.id}>
                      <td>
                        <div className="font-semi text-gray-900">{app.student?.full_name}</div>
                        <div className="text-xs text-gray-500">{app.student?.college_email}</div>
                      </td>
                      <td>
                        <div className="text-sm">{app.student?.college_name}</div>
                        <div className="text-xs text-gray-500">{app.student?.branch} • {app.student?.year && `Year ${app.student.year}`}</div>
                      </td>
                      <td>
                        <Link to={`/opportunity/${app.opportunity_id}`} className="font-medium text-primary hover:underline">
                          {app.opportunity?.title}
                        </Link>
                      </td>
                      <td>{new Date(app.created_at).toLocaleDateString()}</td>
                      <td>
                        <Badge variant={
                          app.status === 'Selected' ? 'success' :
                          app.status === 'Rejected' ? 'danger' :
                          app.status === 'Interview Scheduled' ? 'primary' :
                          app.status === 'Shortlisted' ? 'accent' : 'gray'
                        }>
                          {app.status}
                        </Badge>
                      </td>
                      <td>
                        <select 
                          className="form-input text-xs py-1.5 pl-2 pr-6 h-auto"
                          value={app.status}
                          onChange={(e) => handleStatusChange(app.id, e.target.value)}
                        >
                          <option disabled value={app.status}>Update Status</option>
                          {Object.values(STATUS).map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyApplicants;